import { Stack, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useMemo } from "react";

import type { ChannelName, ChannelStatus } from "../../../../channels/types";
import type { GetPersonsInSegmentJsonPerson } from "../../../types/segment-schema";
import type { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid-pro";
import type { FC } from "react";

export const PreviewDatagrid: FC<{
  channelName: ChannelName;
  persons: GetPersonsInSegmentJsonPerson[];
  connection?: string;
}> = ({ channelName, persons, connection }) => {
  const columns: GridColDef<GetPersonsInSegmentJsonPerson>[] = useMemo(
    () => [
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
      },
      {
        field: "email",
        headerName: "Work Email",
        flex: 1,
      },
      ...(channelName
        ? [
            {
              field: "channels",
              headerName: "Channel Display Name",
              flex: 1,
              valueFormatter: (value: Record<string, ChannelStatus>) => {
                if (!channelName) return "";
                return value[channelName]?.display || "";
              },
              renderCell: (
                params: GridRenderCellParams<
                  GetPersonsInSegmentJsonPerson,
                  GetPersonsInSegmentJsonPerson["channels"]
                >,
              ) => {
                if (!channelName) return "";
                // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                return (params.value as Record<string, ChannelStatus>)[channelName]?.display || "";
              },
            },
          ]
        : []),
    ],
    [channelName],
  );

  const connectionColumns: GridColDef[] = useMemo(
    () => [{ field: "email", headerName: "Email", flex: 1 }],
    [],
  );

  const getDetailPanelContent = useMemo(
    () =>
      connection
        ? (params: GridRowParams<GetPersonsInSegmentJsonPerson>) => {
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            const connections = (params.row.connections || []) as Connection[];

            return (
              <Stack direction="column" spacing={2} sx={{ p: 2, width: "100%" }}>
                <Typography variant="subtitle1">{connection} of</Typography>
                <DataGridPro
                  rows={connections}
                  columns={connectionColumns}
                  hideFooter
                  density="compact"
                />
              </Stack>
            );
          }
        : undefined,
    [connection, connectionColumns],
  );

  return (
    <DataGridPro
      rows={persons}
      columns={columns}
      getDetailPanelContent={connection ? getDetailPanelContent : undefined}
      getDetailPanelHeight={(params: GridRowParams<GetPersonsInSegmentJsonPerson>) =>
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        connection && (params.row.connections as Connection[])?.length
          ? // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            (params.row.connections as Connection[]).length * 52 + 120
          : 0
      }
      pagination
      showToolbar
    />
  );
};

interface Connection {
  id: string;
  email: string;
  employee_id: string | null;
}

export interface ConnectionMessage {
  id: string;
  recipient_first_name: string;
  recipient_last_name: string;
  recipient_email: string;
  subject_first_name: string;
  subject_last_name: string;
}

const connectionMessageColumns: GridColDef<ConnectionMessage>[] = [
  { field: "recipient_first_name", headerName: "Recipient First Name", flex: 1 },
  { field: "recipient_last_name", headerName: "Recipient Last Name", flex: 1 },
  { field: "recipient_email", headerName: "Work Email", flex: 1 },
  { field: "subject_first_name", headerName: "Subject First Name", flex: 1 },
  { field: "subject_last_name", headerName: "Subject Last Name", flex: 1 },
];

/** One row for each message: the connection it goes to and the person it is about. */
export const ConnectionMessagesDatagrid: FC<{ messages: ConnectionMessage[] }> = ({ messages }) => (
  <DataGridPro rows={messages} columns={connectionMessageColumns} pagination showToolbar />
);
